/**
 * ResearchWidget — Research hub for predictive models, backtests, and methodology
 * CSS prefix: rsw-
 */

import { useState } from 'react'
import { BookOpen, Brain, BarChart3, Database, ExternalLink, FileText, ChevronRight } from 'lucide-react'
import './ResearchWidget.css'

const RESEARCH_SECTIONS = {
  models: {
    title: 'Predictive Models',
    description: 'Active and in-development forecasting models',
    items: [
      { label: 'MLB Win Probability Model', status: 'planned', desc: 'Game-level win probability using pitching matchups, park factors, and market data' },
      { label: 'Stuff+ BABIP Model', status: 'planned', desc: 'Pitch shape → BABIP prediction (SABR 2026: challenges DIPS theory)' },
      { label: 'Player Projection System', status: 'planned', desc: 'Bayesian aging curves + injury detection for pitcher/hitter projections' },
      { label: 'Closing Line Value (CLV) Model', status: 'planned', desc: 'Predict where lines will close to identify +EV opening positions' },
      { label: 'Market Efficiency Detector', status: 'planned', desc: 'Cross-book arbitrage + steam detection + limit increase signals' },
      { label: 'Optimal Bet Sizing (Kelly)', status: 'planned', desc: 'Kelly criterion with fractional sizing, bankroll management' },
    ],
  },
  backtests: {
    title: 'Backtesting Results',
    description: 'Historical performance of model strategies',
    items: [
      { label: 'Steam Chasing Strategy', status: 'planned', desc: 'Performance of betting laggard books after sharp line moves' },
      { label: '+EV Detection ROI', status: 'planned', desc: 'Historical returns on positive expected value bets identified by our models' },
      { label: 'Opening Line Value', status: 'planned', desc: 'Returns from capturing early value before market correction' },
      { label: 'Closing Line Value', status: 'planned', desc: 'Does our model consistently beat the closing line? CLV tracking' },
    ],
  },
  sources: {
    title: 'Data Sources & References',
    description: 'Academic papers, methodologies, and data feeds',
    links: [
      { label: 'FanGraphs Research', url: 'https://www.fangraphs.com/research', desc: 'Sabermetric research articles' },
      { label: 'Baseball Prospectus', url: 'https://www.baseballprospectus.com', desc: 'PECOTA, DRC+, analytical deep dives' },
      { label: 'The Hardball Times', url: 'https://tht.fangraphs.com', desc: 'Advanced baseball analysis and history' },
      { label: 'TangoTiger Blog', url: 'https://www.tangotiger.com', desc: 'wOBA, The Book, custom research' },
      { label: 'SABR.org', url: 'https://sabr.org', desc: 'Society for American Baseball Research' },
      { label: 'Pinnacle Resources', url: 'https://www.pinnacle.com/en/betting-resources', desc: 'Betting theory and market analysis' },
      { label: 'Unabated', url: 'https://unabated.com', desc: 'Professional sports betting tools and research' },
      { label: 'The Power Rank', url: 'https://thepowerrank.com', desc: 'Sports analytics and prediction models' },
    ],
  },
  sabr: {
    title: 'SABR 2026 Analytics Conference',
    description: 'Key insights from 32 presentations at the 2026 SABR Analytics Conference',
    highlights: [
      { title: 'Stuff+ Predicts BABIP', desc: 'Pitch shape data predicts contact quality outcomes, challenging traditional DIPS theory. Both Eno Sarris and Derek Carty confirmed independently.' },
      { title: 'Bayesian Age Curves', desc: '36% of pitcher velocity change points align with surgery, 64% with natural aging. Use hierarchical Bayesian modeling with change point detection.' },
      { title: 'Context-Dependent Approach', desc: 'Rice University: 4-model pipeline (outcome prediction, contact quality, state transitions, win probability) for situational optimization.' },
      { title: 'Kalman Filters for Pitchers', desc: 'Isolate true mechanical state from Statcast noise. Hidden state variables: release position, arm angle, extension.' },
      { title: 'Competitive Advantage Shift', desc: 'Vince Gennaro: Diminishing returns in physics-based analytics. Behavioral/interpersonal domain is the new frontier.' },
      { title: 'In-Game Biomechanics', desc: 'Markerless motion capture (KinaTrax/Hawk-Eye) producing in-game data at every affiliate. The new gold standard.' },
    ],
  },
}

const STATUS_STYLES = {
  active: { bg: 'rgba(0, 200, 83, 0.12)', color: '#00C853', label: 'ACTIVE' },
  planned: { bg: 'rgba(253, 181, 21, 0.12)', color: '#FDB515', label: 'PLANNED' },
  testing: { bg: 'rgba(0, 184, 212, 0.12)', color: '#00B8D4', label: 'TESTING' },
}

export default function ResearchWidget({ tab = 'models' }) {
  const section = RESEARCH_SECTIONS[tab]
  if (!section) return null

  return (
    <div className="widget rsw-widget">
      <div className="widget-header">
        <BookOpen size={16} />
        <h2 className="widget-title">{section.title}</h2>
      </div>

      <div className="rsw-desc">{section.description}</div>

      {/* Models & Backtests view */}
      {section.items && (
        <div className="rsw-list">
          {section.items.map((item, i) => {
            const st = STATUS_STYLES[item.status] || STATUS_STYLES.planned
            return (
              <div key={i} className="rsw-item">
                <div className="rsw-item-header">
                  <Brain size={14} className="rsw-item-icon" />
                  <span className="rsw-item-label">{item.label}</span>
                  <span className="rsw-item-status" style={{ background: st.bg, color: st.color }}>
                    {st.label}
                  </span>
                </div>
                <p className="rsw-item-desc">{item.desc}</p>
              </div>
            )
          })}
        </div>
      )}

      {/* Sources view */}
      {section.links && (
        <div className="rsw-list">
          {section.links.map((link, i) => (
            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="rsw-link">
              <div className="rsw-link-info">
                <span className="rsw-link-label">{link.label}</span>
                <span className="rsw-link-desc">{link.desc}</span>
              </div>
              <ExternalLink size={14} className="rsw-link-icon" />
            </a>
          ))}
        </div>
      )}

      {/* SABR highlights */}
      {section.highlights && (
        <div className="rsw-list">
          {section.highlights.map((h, i) => (
            <div key={i} className="rsw-highlight">
              <h4 className="rsw-highlight-title">{h.title}</h4>
              <p className="rsw-highlight-desc">{h.desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
